import { dayOf, toCalendarDay } from '@ghp/domain'

/**
 * L'arithmétique de l'agenda — **jours de calendrier**, jamais instants.
 *
 * ⚠️ Un jour se recompose **par ses parts**, jamais par `new Date('2026-08-04')` : cette forme
 * est lue comme minuit UTC, donc comme la veille à l'ouest de Greenwich. La même règle vaut pour
 * l'arithmétique : on décale des **composants** (`new Date(y, m - 1, d + n)`), ce qui traverse un
 * changement d'heure sans dériver, là où ajouter 86 400 000 ms fait perdre ou gagner une heure
 * deux dimanches par an.
 */

/** Les six jours ouvrés que la semaine affiche, lundi → samedi (maquette validée). */
const WORKING_DAYS = 6

/** Les sept jours d'une grille, lundi → dimanche : ce que le mois affiche, et ce qu'on lit. */
const WEEK_DAYS = 7

/** Une plage de jours, **bornes comprises** — la forme que `scheduledBetween` attend. */
export interface DayRange {
  from: string
  to: string
}

/** Aujourd'hui **à Paris**, le calendrier où l'agenda range ses rendez-vous (`calendarDayOf`). */
export function today(): string {
  return dayOf(new Date())
}

/**
 * Le `Date` local d'un jour de calendrier, ou `null` s'il est illisible — une valeur venue d'une
 * URL ou d'un cache ne doit pas faire tomber l'agenda (M21).
 */
export function dateOfDay(day: string): Date | null {
  const [year, month, date] = day.split('-').map(Number)

  if (year === undefined || month === undefined || date === undefined) return null
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(date)) return null

  const parsed = new Date(year, month - 1, date)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Ce jour est-il **exactement** un jour du calendrier ?
 *
 * ⚠️ `dateOfDay` ne suffit pas à valider une entrée : `new Date(2026, 12, 45)` ne lève pas, il
 * reporte le débordement — `2026-13-45` deviendrait le 14 février 2027 sans qu'un seul appel
 * n'échoue. Le test est donc l'aller-retour : le jour recomposé doit se réécrire à l'identique.
 */
export function isCalendarDay(day: string): boolean {
  const date = dateOfDay(day)

  return date !== null && toCalendarDay(date) === day
}

/** Le jour décalé de `count` jours — `count` peut être négatif. */
export function shiftDays(day: string, count: number): string {
  const date = dateOfDay(day)

  if (date === null) return day

  return toCalendarDay(new Date(date.getFullYear(), date.getMonth(), date.getDate() + count))
}

/**
 * Le jour décalé de `count` mois, **borné au dernier jour du mois d'arrivée** : sans cette borne,
 * « mois suivant » depuis le 31 janvier atterrirait en mars — JavaScript reportant le
 * débordement — et l'agent sauterait février sans comprendre pourquoi.
 */
export function shiftMonths(day: string, count: number): string {
  const date = dateOfDay(day)

  if (date === null) return day

  const target = new Date(date.getFullYear(), date.getMonth() + count, 1)
  const lastDate = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()

  return toCalendarDay(
    new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDate)),
  )
}

/** Le lundi de la semaine d'un jour. */
export function mondayOf(day: string): string {
  const date = dateOfDay(day)

  if (date === null) return day

  // `getDay()` rend 0 pour dimanche : ce décalage ramène lundi à 0 et dimanche à 6.
  return shiftDays(day, -((date.getDay() + 6) % 7))
}

/** Les six colonnes de la vue Semaine, lundi → samedi. */
export function workingWeekOf(day: string): string[] {
  const monday = mondayOf(day)

  return Array.from({ length: WORKING_DAYS }, (_, index) => shiftDays(monday, index))
}

/**
 * Ce que la vue Semaine **demande à Dexie** : lundi → dimanche, donc un jour de plus que ses six
 * colonnes.
 *
 * ⚠️ Ce jour de plus n'est pas une marge de confort. Un rendez-vous posé un dimanche existe dans
 * la base — rien ne l'interdit — et n'a aucune colonne où se poser : la plage le ramène, et
 * l'écran le signale **hors grille** plutôt que de le taire (M21). Un rendez-vous qu'aucun écran
 * ne montre est un rendez-vous manqué.
 */
export function weekRangeOf(day: string): DayRange {
  const monday = mondayOf(day)

  return { from: monday, to: shiftDays(monday, WEEK_DAYS - 1) }
}

/** Les semaines de la grille du mois, chacune de sept jours, lundi → dimanche. */
export function monthWeeksOf(day: string): string[][] {
  const date = dateOfDay(day)

  if (date === null) return []

  const firstOfMonth = toCalendarDay(new Date(date.getFullYear(), date.getMonth(), 1))
  const lastOfMonth = toCalendarDay(new Date(date.getFullYear(), date.getMonth() + 1, 0))
  const weeks: string[][] = []

  // Les jours étant en `AAAA-MM-JJ`, la comparaison de chaînes **est** la comparaison
  // chronologique : c'est la même propriété qui rend l'index Dexie lisible en plage.
  for (
    let start = mondayOf(firstOfMonth);
    start <= lastOfMonth;
    start = shiftDays(start, WEEK_DAYS)
  ) {
    weeks.push(Array.from({ length: WEEK_DAYS }, (_, index) => shiftDays(start, index)))
  }

  return weeks
}

/** Ce que la vue Mois demande à Dexie : la grille entière, débords des mois voisins compris. */
export function monthRangeOf(day: string): DayRange {
  const weeks = monthWeeksOf(day)
  const first = weeks[0]?.[0]
  const last = weeks.at(-1)?.at(-1)

  return first === undefined || last === undefined
    ? { from: day, to: day }
    : { from: first, to: last }
}

/** Ce jour appartient-il au mois de l'ancre ? Les débords de grille se peignent en retrait. */
export function isSameMonth(day: string, anchor: string): boolean {
  const left = dateOfDay(day)
  const right = dateOfDay(anchor)

  if (left === null || right === null) return false

  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth()
}
